import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Typography, Paper, Box, Button, CircularProgress, Alert,
  Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, useMediaQuery, useTheme, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  ArrowBack, Edit, PictureAsPdf, History, Close, Hotel,
  AccountBalanceWallet, Person, WhatsApp, DeleteOutline, AttachFile,
  Fullscreen, FullscreenExit, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { format, differenceInDays, isValid } from 'date-fns';
import { getGuestById, getAuditTrail, getDocuments, deleteDocument, sendBookingWhatsApp, sendBookingEmail, Booking, BookingDocument } from '../services/api';
import { generateConfirmationPDF, generateConfirmationPDFBlob } from '../utils/pdfGenerator';
import { UPLOAD_PATHS } from '../constants/api';
import {
  AUDIT_FIELD_LABEL,
  AUDIT_FIELD_KEY,
  AUDIT_SKIP_FIELDS,
  AUDIT_SUMMARY_FIELDS,
  BOOKING_STATUS,
  BOOKING_TYPE,
  BOOKING_TYPE_LABEL,
  STATUS_COLORS,
  STATUS_NOTIFICATION_LABEL,
} from '../constants/booking';
import {
  NOTIFICATION_BUTTON_LABEL,
  NOTIFICATION_CHANNEL,
  NOTIFICATION_CHANNEL_LABEL,
  NOTIFICATION_DIALOG_TITLE,
  NOTIFICATION_TOAST,
  AUDIT_ACTION,
  AUDIT_DISPLAY,
  DETAIL_MESSAGE,
  NotificationChannel,
} from '../constants/notification';
import { AMOUNT_OVERRIDE_REASON_LABEL, getChildRateFactor } from '../constants/pricing';
import {
  GUEST_DETAIL_AUDIT_LABEL,
  GUEST_DETAIL_BOOLEAN_LABEL,
  GUEST_DETAIL_CONFIRMATION,
  GUEST_DETAIL_FALLBACK,
  GUEST_DETAIL_FIELD_LABEL,
  GUEST_DETAIL_MESSAGE,
  GUEST_DETAIL_REFUND_STATUS,
  GUEST_DETAIL_TABLE_LABEL,
  GUEST_DETAIL_TEXT,
} from '../constants/guestDetail';
import { APP_ROUTES } from '../constants/routes';
import { TOAST_SEVERITY } from '../constants/toast';
import { USER_ROLE } from '../constants/userManagement';
import { useAuthState } from '../store/authHooks';
import { useToastActions } from '../store/toastHooks';

// Renders a label/value pair with consistent Guest Detail spacing and fallback display.
const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 1, borderBottom: '1px solid #e3f0fb' }}>
    <Typography component="span" sx={{ width: 200, minWidth: 160, fontWeight: 600, color: 'primary.dark', fontSize: '0.9rem' }}>{label}</Typography>
    <Typography component="span" sx={{ color: '#333', fontSize: '0.9rem' }}>{value || '-'}</Typography>
  </Box>
);

const CHILD_RATE_FACTOR = getChildRateFactor();

// Calculates the system room total from day-wise rooms, children rate, and driver charges.
const calculateRoomTotal = (booking: Booking) =>
  (booking.dayBookings || []).reduce((total, day) => {
    const driverCount = Number(booking.NO_DRIVERS) || 0;
    const roomTotal = (day.rooms || []).reduce((dayTotal, room) => {
      const adults = Number(room.NO_ADULTS_IN_ROOM) || 0;
      const children = Number(room.NO_CHILDREN_IN_ROOM) || 0;
      const amount = Number(room.AMOUNT_PER_PERSON) || 0;
      return dayTotal + (adults * amount) + (children * amount * CHILD_RATE_FACTOR);
    }, 0);
    return total + roomTotal + ((Number(day.DRIVER_AMOUNT_PER_DAY) || 0) * driverCount);
  }, 0);

// Parses audit JSON values defensively because older audit rows can store invalid text.
const parseAuditValues = (values: any) => {
  if (!values) return null;
  if (typeof values !== 'string') return values;
  try {
    return JSON.parse(values);
  } catch {
    return null;
  }
};

const GuestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { canWrite, user } = useAuthState();
  const { showToast } = useToastActions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<any[]>([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [docs, setDocs] = useState<BookingDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [deletingDoc, setDeletingDoc] = useState<number | null>(null);
  const [sendingWA, setSendingWA] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ type: NotificationChannel } | null>(null);
  const [statusAcknowledged, setStatusAcknowledged] = useState(false);
  const [viewDoc, setViewDoc] = useState<BookingDocument | null>(null);
  const [viewFullscreen, setViewFullscreen] = useState(false);

  // Closes the document preview and resets fullscreen state for the next document.
  const closeViewer = useCallback(() => { setViewDoc(null); setViewFullscreen(false); }, []);
  const viewIndex = useMemo(() => viewDoc ? docs.findIndex(d => d.id === viewDoc.id) : -1, [docs, viewDoc]);
  // Moves the document viewer to the previous uploaded document when available.
  const goPrev = useCallback(() => viewIndex > 0 && setViewDoc(docs[viewIndex - 1]), [docs, viewIndex]);
  // Moves the document viewer to the next uploaded document when available.
  const goNext = useCallback(() => viewIndex < docs.length - 1 && setViewDoc(docs[viewIndex + 1]), [docs, viewIndex]);
  // On mobile always use fullscreen; on desktop use toggle state
  const isFullscreen = useMemo(() => isMobile || viewFullscreen, [isMobile, viewFullscreen]);

  useEffect(() => {
    if (id) {
      getGuestById(parseInt(id))
        .then(data => { setBooking(data); setLoading(false); })
        .catch(() => { setError(GUEST_DETAIL_MESSAGE.LOAD_BOOKING_FAILED); setLoading(false); });
      getAuditTrail(parseInt(id))
        .then(setAudit)
        .catch(() => setAudit([]));
      getDocuments(parseInt(id))
        .then(data => { setDocs(data); setDocsLoading(false); })
        .catch(() => setDocsLoading(false));
    }
  }, [id]);

  // Deletes one uploaded document after admin confirmation and updates local document state.
  const handleDeleteDoc = useCallback(async (docId: number) => {
    if (!window.confirm(GUEST_DETAIL_MESSAGE.DELETE_DOCUMENT_CONFIRM)) return;
    setDeletingDoc(docId);
    try {
      await deleteDocument(parseInt(id!), docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
      showToast(GUEST_DETAIL_MESSAGE.DOCUMENT_DELETED, TOAST_SEVERITY.SUCCESS);
    } catch {
      showToast(GUEST_DETAIL_MESSAGE.DOCUMENT_DELETE_FAILED, TOAST_SEVERITY.ERROR);
    } finally {
      setDeletingDoc(null);
    }
  }, [id, showToast]);

  // Converts generated PDF blobs to base64 for notification APIs that accept attachments.
  const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  // Opens the notification confirmation dialog and resets status acknowledgement.
  const openConfirmDialog = useCallback((type: NotificationChannel) => {
    setStatusAcknowledged(false);
    setConfirmDialog({ type });
  }, []);

  // Closes the notification confirmation dialog and clears acknowledgement state.
  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(null);
    setStatusAcknowledged(false);
  }, []);

  // Sends WhatsApp notification, optionally attaching PDF for normal confirmations.
  const handleSendWhatsApp = async () => {
    if (!booking) return;
    closeConfirmDialog();
    setSendingWA(true);
    try {
      const base64 = requiresStatusAcknowledgement
        ? ''
        : await blobToBase64(await generateConfirmationPDFBlob(booking));
      const { message, pdfError } = await sendBookingWhatsApp(booking.BOOKINGID!, base64);
      showToast(message || NOTIFICATION_TOAST.WHATSAPP_SENT, TOAST_SEVERITY.SUCCESS);
      if (pdfError) setTimeout(() => showToast(pdfError, TOAST_SEVERITY.WARNING), 500);
    } catch (err: any) {
      showToast(err?.response?.data?.message || NOTIFICATION_TOAST.WHATSAPP_FAILED, TOAST_SEVERITY.ERROR);
    } finally {
      setSendingWA(false);
    }
  };

  // Sends email notification, optionally attaching PDF for normal confirmations.
  const handleSendEmail = async () => {
    if (!booking) return;
    closeConfirmDialog();
    setSendingEmail(true);
    try {
      const base64 = requiresStatusAcknowledgement
        ? ''
        : await blobToBase64(await generateConfirmationPDFBlob(booking));
      await sendBookingEmail(booking.BOOKINGID!, base64);
      showToast(NOTIFICATION_TOAST.EMAIL_SENT, TOAST_SEVERITY.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.message || NOTIFICATION_TOAST.EMAIL_FAILED, TOAST_SEVERITY.ERROR);
    } finally {
      setSendingEmail(false);
    }
  };

  // Refreshes audit records before opening the audit trail dialog.
  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const data = await getAuditTrail(parseInt(id!));
      setAudit(data);
    } catch {
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  };

  // Formats date-only values for display, returning a fallback for missing/invalid dates.
  const fmtDate = (d?: string) => {
    if (!d) return '-';
    const dt = new Date(d);
    return isValid(dt) ? format(dt, 'dd MMM yyyy') : '-';
  };

  // Formats date-time values and handles Hostinger/MySQL UTC datetime strings.
  const fmtDateTime = (d?: string) => {
    if (!d) return '-';
    // mysql2 returns DATETIME as "YYYY-MM-DD HH:MM:SS" (no TZ) — these are UTC on Hostinger
    // Appending 'Z' forces correct UTC parsing so local (IST) time shows properly
    const str = typeof d === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(d)
      ? d.replace(' ', 'T') + 'Z'
      : d;
    const dt = new Date(str);
    return isValid(dt) ? format(dt, 'dd MMM yyyy, hh:mm a') : '-';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress size={52} /></Box>;
  if (error || !booking) return (
    <Container sx={{ mt: 4 }}>
      <Alert severity={TOAST_SEVERITY.ERROR}>{error || DETAIL_MESSAGE.BOOKING_NOT_FOUND}</Alert>
      <Button onClick={() => navigate(APP_ROUTES.HOME, { state: location.state })} startIcon={<ArrowBack />} sx={{ mt: 2 }}>{GUEST_DETAIL_TEXT.BUTTON_BACK}</Button>
    </Container>
  );

  const numDays = differenceInDays(new Date(booking.CHECKOUT_DATE), new Date(booking.CHECKIN_DATE));
  const remaining = (booking.TOTAL_AMOUNT || 0) - (booking.ADVANCE_AMOUNT || 0);
  const systemCalculatedTotal = calculateRoomTotal(booking);
  const amountDifference = (booking.TOTAL_AMOUNT || 0) - systemCalculatedTotal;
  const amountOverrideAudit = audit.find(a => a.ACTION === AUDIT_ACTION.AMOUNT_OVERRIDE);
  const hasAmountDifference = Math.abs(amountDifference) > 0.01 && !!amountOverrideAudit;
  const amountOverrideDetails = parseAuditValues(amountOverrideAudit?.NEW_VALUES);
  const displayedAmountDifference = Number(amountOverrideDetails?.difference ?? amountDifference) || 0;
  const amountOverrideReason = amountOverrideDetails?.reason
    ? AMOUNT_OVERRIDE_REASON_LABEL[amountOverrideDetails.reason] || amountOverrideDetails.reason
    : '-';
  const amountOverrideNote = amountOverrideDetails?.note || '-';
  const totalMembers = (booking.NO_ADULTS || 0) + (booking.NO_CHILDREN || 0) + (booking.NO_DRIVERS || 0);
  const requiresStatusAcknowledgement = booking.STATUS === BOOKING_STATUS.CANCELLED || booking.STATUS === BOOKING_STATUS.RESCHEDULED;
  const statusLabel = booking.STATUS === BOOKING_STATUS.CANCELLED
    ? STATUS_NOTIFICATION_LABEL[BOOKING_STATUS.CANCELLED]
    : STATUS_NOTIFICATION_LABEL[BOOKING_STATUS.RESCHEDULED];
  const notificationTypeLabel = confirmDialog?.type
    ? NOTIFICATION_CHANNEL_LABEL[confirmDialog.type]
    : NOTIFICATION_CHANNEL_LABEL[NOTIFICATION_CHANNEL.EMAIL];
  const confirmSendDisabled = requiresStatusAcknowledgement && !statusAcknowledged;

  return (
    <Box sx={{ background: '#eaf4fb', minHeight: '100vh', pb: 6 }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg,#023e8a,#0077b6)', py: 3, px: { xs: 2, md: 6 }, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(APP_ROUTES.HOME, { state: location.state })} sx={{ color: '#fff', mb: 1 }}>{GUEST_DETAIL_TEXT.BUTTON_BACK}</Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>{booking.GUEST_NAME}</Typography>
            <Typography sx={{ color: '#90e0ef' }}>Booking #{booking.BOOKINGID}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={booking.STATUS?.toUpperCase()} color={STATUS_COLORS[booking.STATUS] || 'default'} sx={{ fontWeight: 700 }} />
            {canWrite && (
              <Button variant="outlined" startIcon={<Edit />} onClick={() => navigate(APP_ROUTES.EDIT_BOOKING(booking.BOOKINGID!), { state: location.state })}
                sx={{ color: '#fff', borderColor: '#fff' }}>{GUEST_DETAIL_TEXT.BUTTON_EDIT}</Button>
            )}
            <Button variant="contained" startIcon={<PictureAsPdf />} onClick={async () => { await generateConfirmationPDF(booking); }}
              sx={{ background: '#52b788', '&:hover': { background: '#40916c' } }}>{GUEST_DETAIL_TEXT.BUTTON_PDF}</Button>
            {canWrite && (
              <Button variant="contained" startIcon={sendingWA ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <WhatsApp />}
                onClick={() => openConfirmDialog(NOTIFICATION_CHANNEL.WHATSAPP)} disabled={sendingWA}
                sx={{ background: '#25D366', '&:hover': { background: '#1ebe57' } }}>
                {sendingWA ? NOTIFICATION_BUTTON_LABEL.SENDING : NOTIFICATION_BUTTON_LABEL.WHATSAPP}
              </Button>
            )}
            {canWrite && booking.EMAIL && (
              <Button variant="contained" startIcon={sendingEmail ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                onClick={() => openConfirmDialog(NOTIFICATION_CHANNEL.EMAIL)} disabled={sendingEmail}
                sx={{ background: '#e65100', '&:hover': { background: '#bf360c' } }}>
                {sendingEmail ? NOTIFICATION_BUTTON_LABEL.SENDING : NOTIFICATION_BUTTON_LABEL.EMAIL}
              </Button>
            )}
            <Button variant="outlined" startIcon={<History />} onClick={openAudit}
              sx={{ color: '#fff', borderColor: '#fff' }}>{GUEST_DETAIL_TEXT.BUTTON_AUDIT}</Button>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={3}>
          {/* Guest detail section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
                <Person sx={{ verticalAlign: 'middle', mr: 1 }} />{GUEST_DETAIL_TEXT.SECTION_GUEST_DETAILS}
              </Typography>
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.NAME} value={booking.GUEST_NAME} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.PHONE} value={booking.PHONE} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.EMAIL} value={booking.EMAIL} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.ADDRESS} value={booking.ADDRESS} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.BOOKED_BY} value={booking.BOOKED_BY} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.REMARKS} value={booking.REMARKS} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.STATUS} value={<Chip label={booking.STATUS} color={STATUS_COLORS[booking.STATUS] || 'default'} size="small" />} />
              {booking.STATUS === BOOKING_STATUS.CANCELLED && (
                <>
                  <InfoRow label={GUEST_DETAIL_FIELD_LABEL.CANCELLATION_REASON} value={booking.CANCELLATION_REASON || GUEST_DETAIL_FALLBACK.EMPTY_DASH} />
                  <InfoRow label={GUEST_DETAIL_FIELD_LABEL.IS_REFUNDED} value={
                    booking.IS_REFUNDED === GUEST_DETAIL_REFUND_STATUS.YES ? (
                      <Chip label={GUEST_DETAIL_BOOLEAN_LABEL.YES} color={TOAST_SEVERITY.SUCCESS} size="small" />
                    ) : booking.IS_REFUNDED === GUEST_DETAIL_REFUND_STATUS.NO ? (
                      <Chip label={GUEST_DETAIL_BOOLEAN_LABEL.NO} color={TOAST_SEVERITY.ERROR} size="small" />
                    ) : GUEST_DETAIL_FALLBACK.EMPTY_DASH
                  } />
                  {booking.IS_REFUNDED === GUEST_DETAIL_REFUND_STATUS.YES && (
                    <InfoRow label={GUEST_DETAIL_FIELD_LABEL.REFUNDED_AMOUNT} value={`₹ ${(booking.REFUNDED_AMOUNT ?? 0).toFixed(2)}`} />
                  )}
                </>
              )}
            </Paper>
          </Grid>

          {/* Stay detail section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
                {GUEST_DETAIL_TEXT.SECTION_STAY_DETAILS}
              </Typography>
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.CHECK_IN} value={fmtDate(booking.CHECKIN_DATE)} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.CHECK_OUT} value={fmtDate(booking.CHECKOUT_DATE)} />
              <InfoRow
                label={AUDIT_FIELD_LABEL.BOOKING_TYPE}
                value={<Chip label={BOOKING_TYPE_LABEL[booking.BOOKING_TYPE || BOOKING_TYPE.ADVANCE]} color={booking.BOOKING_TYPE === BOOKING_TYPE.WALK_IN ? 'info' : 'default'} size="small" />}
              />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.NO_OF_DAYS} value={numDays} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.ADULTS} value={booking.NO_ADULTS} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.CHILDREN} value={booking.NO_CHILDREN} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.DRIVERS} value={booking.NO_DRIVERS} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.TOTAL_MEMBERS} value={totalMembers} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.CREATED_BY} value={booking.CREATEDBY} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.CREATED_ON} value={fmtDateTime(booking.CREATEDON)} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.LAST_MODIFIED} value={fmtDateTime(booking.LASTMODIFIEDON)} />
              <InfoRow label={GUEST_DETAIL_FIELD_LABEL.MODIFIED_BY} value={booking.LASTMODIFIEDBY} />
            </Paper>
          </Grid>

          {/* Financial summary section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg,#023e8a,#0077b6)', color: '#fff' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                <AccountBalanceWallet sx={{ verticalAlign: 'middle', mr: 1 }} />{GUEST_DETAIL_TEXT.SECTION_FINANCIALS}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography>{GUEST_DETAIL_FIELD_LABEL.TOTAL_AMOUNT}</Typography>
                <Typography fontWeight={700}>₹ {booking.TOTAL_AMOUNT?.toFixed(2)}</Typography>
              </Box>
              {hasAmountDifference && (
                <Box
                  sx={{
                    my: 1.2,
                    p: 1.2,
                    borderRadius: 1,
                    bgcolor: 'rgba(211, 47, 47, 0.18)',
                    border: '1px solid rgba(255, 205, 210, 0.8)',
                  }}
                >
                  <Typography fontSize="0.82rem" fontWeight={800} sx={{ color: '#ffcdd2' }}>
                    {GUEST_DETAIL_FIELD_LABEL.DIFFERENCE}: {displayedAmountDifference >= 0 ? '+' : '-'}₹ {Math.abs(displayedAmountDifference).toFixed(2)}
                  </Typography>
                  <Typography fontSize="0.78rem" sx={{ mt: 0.5 }}>
                    <strong>{GUEST_DETAIL_FIELD_LABEL.REASON}:</strong> {amountOverrideReason}
                  </Typography>
                  <Typography fontSize="0.78rem">
                    <strong>{GUEST_DETAIL_FIELD_LABEL.NOTE}:</strong> {amountOverrideNote}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography>{GUEST_DETAIL_FIELD_LABEL.ADVANCE_PAID}</Typography>
                <Typography fontWeight={700} sx={{ color: '#74c69d' }}>₹ {booking.ADVANCE_AMOUNT?.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography fontWeight={700}>{GUEST_DETAIL_FIELD_LABEL.REMAINING}</Typography>
                <Typography fontWeight={800} fontSize="1.1rem" sx={{ color: remaining > 0 ? '#f4a261' : '#74c69d' }}>
                  ₹ {remaining.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Room allocation section */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
                <Hotel sx={{ verticalAlign: 'middle', mr: 1 }} />{GUEST_DETAIL_TEXT.SECTION_ROOM_ALLOCATION}
              </Typography>
              {(booking.dayBookings || []).length === 0 ? (
                <Typography color="text.secondary">{GUEST_DETAIL_TEXT.NO_ROOM_ALLOCATIONS}</Typography>
              ) : (
                booking.dayBookings!.map(day => (
                  <Box key={day.STAY_DATE} sx={{ mb: 2 }}>
                    <Chip label={fmtDate(day.STAY_DATE)} color="primary" size="small" sx={{ mb: 1 }} />
                    {(Number(booking.NO_DRIVERS) || 0) > 0 && (
                      <Box
                        sx={{
                          mb: 1.2,
                          p: 1.2,
                          borderRadius: 1,
                          bgcolor: '#fff3e0',
                          border: '1px solid #ffb74d',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography fontSize="0.86rem" fontWeight={800} sx={{ color: '#e65100' }}>
                          {GUEST_DETAIL_TEXT.DRIVER_CHARGE}
                        </Typography>
                        <Typography fontSize="0.86rem" fontWeight={800} sx={{ color: '#bf360c' }}>
                          ₹ {(Number(day.DRIVER_AMOUNT_PER_DAY) || 0).toFixed(2)} x {Number(booking.NO_DRIVERS) || 0} = ₹ {((Number(day.DRIVER_AMOUNT_PER_DAY) || 0) * (Number(booking.NO_DRIVERS) || 0)).toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f0f7ff' }}>
                            <TableCell sx={{ fontWeight: 700 }}>{GUEST_DETAIL_TABLE_LABEL.ROOM_TYPE}</TableCell>
	                            <TableCell sx={{ fontWeight: 700 }}>{GUEST_DETAIL_TABLE_LABEL.ROOM_NO}</TableCell>
	                            <TableCell sx={{ fontWeight: 700 }} align="center">{GUEST_DETAIL_TABLE_LABEL.COUNT}</TableCell>
	                            <TableCell sx={{ fontWeight: 700 }} align="center">{GUEST_DETAIL_TABLE_LABEL.ADULTS}</TableCell>
	                            <TableCell sx={{ fontWeight: 700 }} align="center">{GUEST_DETAIL_TABLE_LABEL.CHILDREN}</TableCell>
	                            <TableCell sx={{ fontWeight: 700 }}>{GUEST_DETAIL_TABLE_LABEL.AMOUNT_PER_PERSON}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {day.rooms.map((room, i) => (
                            <TableRow key={i}>
                              <TableCell>{room.ROOM_TYPE === GUEST_DETAIL_FALLBACK.OTHER_ROOM_TYPE ? room.CUSTOM_ROOM_TYPE || GUEST_DETAIL_FALLBACK.OTHER_ROOM_TYPE : room.ROOM_TYPE}</TableCell>
	                              <TableCell>{room.ROOM_NUMBER}</TableCell>
	                              <TableCell align="center">{room.NO_OF_ROOMS}</TableCell>
	                              <TableCell align="center">{room.NO_ADULTS_IN_ROOM || 0}</TableCell>
	                              <TableCell align="center">{room.NO_CHILDREN_IN_ROOM || 0}</TableCell>
	                              <TableCell>₹ {room.AMOUNT_PER_PERSON?.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Documents Section */}
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark', fontWeight: 700 }}>
            <AttachFile sx={{ verticalAlign: 'middle', mr: 1 }} />{GUEST_DETAIL_TEXT.SECTION_DOCUMENTS}
          </Typography>
          {docsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
          ) : docs.length === 0 ? (
            <Typography color="text.secondary" fontSize="0.9rem">{GUEST_DETAIL_TEXT.NO_DOCUMENTS}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {docs.map(doc => {
                const isImage = doc.mime_type.startsWith('image/');
                const url = UPLOAD_PATHS.BOOKING_DOCUMENT(doc.booking_id, doc.filename);
                return (
                  <Box key={doc.id} sx={{
                    width: 160, border: '1px solid #d0e4f5', borderRadius: 2,
                    overflow: 'hidden', position: 'relative',
                  }}>
                    {/* Thumbnail — click opens in-page viewer */}
                    <Box
                      onClick={() => setViewDoc(doc)}
                      sx={{ display: 'block', height: 120, bgcolor: '#f0f7ff', cursor: 'pointer' }}
                    >
                      {isImage ? (
                        <Box component="img" src={url} alt={doc.original_name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <PictureAsPdf sx={{ fontSize: 48, color: '#c62828' }} />
                        </Box>
                      )}
                    </Box>

                    {/* Caption */}
                    <Box sx={{ p: 1 }}>
                      <Typography fontSize="0.72rem" noWrap title={doc.original_name}>{doc.original_name}</Typography>
                      <Typography fontSize="0.65rem" color="text.secondary">
                        {Math.round(doc.file_size / 1024)} KB · {doc.uploaded_by}
                      </Typography>
                    </Box>

                    {/* Admin delete button */}
                    {user?.role === USER_ROLE.ADMIN && (
                      <Tooltip title={GUEST_DETAIL_TEXT.DOCUMENT_DELETE_TOOLTIP}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDoc(doc.id)}
                          disabled={deletingDoc === doc.id}
                          sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#ffebee' } }}
                        >
                          {deletingDoc === doc.id
                            ? <CircularProgress size={14} />
                            : <DeleteOutline fontSize="small" color={TOAST_SEVERITY.ERROR} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Document Viewer Dialog */}
      {viewDoc && (() => {
        const isImage = viewDoc.mime_type.startsWith('image/');
        const url = UPLOAD_PATHS.BOOKING_DOCUMENT(viewDoc.booking_id, viewDoc.filename);
        return (
          <Dialog open onClose={closeViewer}
            maxWidth="lg" fullWidth fullScreen={isFullscreen}
            PaperProps={{ sx: { borderRadius: isFullscreen ? 0 : 3, overflow: 'hidden' } }}>
            <DialogTitle sx={{ background: 'linear-gradient(90deg,#023e8a,#0077b6)', color: '#fff', py: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={700} noWrap sx={{ flex: 1, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  {viewDoc.original_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Typography fontSize="0.78rem" sx={{ color: '#90e0ef', mr: 0.5 }}>
                    {viewIndex + 1}/{docs.length}
                  </Typography>
                  <Tooltip title={GUEST_DETAIL_TEXT.VIEWER_PREVIOUS}><span>
                    <IconButton onClick={goPrev} disabled={viewIndex === 0} size="small"
                      sx={{ color: '#fff', '&.Mui-disabled': { color: '#ffffff40' } }}>
                      <ChevronLeft />
                    </IconButton>
                  </span></Tooltip>
                  <Tooltip title={GUEST_DETAIL_TEXT.VIEWER_NEXT}><span>
                    <IconButton onClick={goNext} disabled={viewIndex === docs.length - 1} size="small"
                      sx={{ color: '#fff', '&.Mui-disabled': { color: '#ffffff40' } }}>
                      <ChevronRight />
                    </IconButton>
                  </span></Tooltip>
                  {!isMobile && (
                    <Tooltip title={viewFullscreen ? GUEST_DETAIL_TEXT.VIEWER_EXIT_FULLSCREEN : GUEST_DETAIL_TEXT.VIEWER_FULLSCREEN}>
                      <IconButton onClick={() => setViewFullscreen(f => !f)} size="small" sx={{ color: '#fff' }}>
                        {viewFullscreen ? <FullscreenExit /> : <Fullscreen />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton onClick={closeViewer} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{
              p: 0, bgcolor: '#111',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              minHeight: isFullscreen ? 0 : 400,
              flex: isFullscreen ? 1 : undefined,
            }}>
              {isImage ? (
                <Box component="img" src={url} alt={viewDoc.original_name} sx={{
                  maxWidth: '100%',
                  maxHeight: isFullscreen ? 'calc(100vh - 112px)' : '75vh',
                  objectFit: 'contain', display: 'block',
                }} />
              ) : (
                <Box component="iframe" src={url} title={viewDoc.original_name} sx={{
                  width: '100%',
                  height: isFullscreen ? 'calc(100vh - 112px)' : '75vh',
                  border: 'none',
                }} />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 0.8, flexWrap: 'wrap', gap: 0.5 }}>
              <Typography fontSize="0.72rem" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {Math.round(viewDoc.file_size / 1024)} KB · {viewDoc.uploaded_by}
              </Typography>
              <Button size="small" onClick={closeViewer}>{GUEST_DETAIL_TEXT.BUTTON_CLOSE}</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Audit Dialog */}
      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(90deg,#023e8a,#0077b6)', color: '#fff', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {GUEST_DETAIL_TEXT.AUDIT_TITLE_PREFIX} — Booking #{booking.BOOKINGID}
            <IconButton onClick={() => setAuditOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : audit.length === 0 ? (
            <Typography>{GUEST_DETAIL_TEXT.NO_AUDIT_RECORDS}</Typography>
          ) : (
            audit.map((a, i) => {
              const oldObj = a.OLD_VALUES ? (typeof a.OLD_VALUES === 'string' ? JSON.parse(a.OLD_VALUES) : a.OLD_VALUES) : null;
              const newObj = a.NEW_VALUES ? (typeof a.NEW_VALUES === 'string' ? JSON.parse(a.NEW_VALUES) : a.NEW_VALUES) : null;

              // Collect all top-level scalar fields (skip dayBookings array)
              const SKIP = new Set<string>(AUDIT_SKIP_FIELDS);
              const SUMMARY_FIELDS = new Set<string>(AUDIT_SUMMARY_FIELDS);

              const allKeys = Array.from(new Set([
                ...Object.keys(oldObj || {}),
                ...Object.keys(newObj || {}),
              ])).filter(k => !SKIP.has(k));

              // Treats null/empty/zero audit values as equivalent for field-change comparison.
              const isEmptyOrZero = (v: any) =>
                v === null || v === undefined || v === '' || Number(v) === 0;

              // Normalizes audit scalar values so numeric/date formatting differences do not create false changes.
              const normalize = (v: any) => {
                if (v === null || v === undefined) return '';
                const s = String(v);
                if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(s)) {
                  const d = new Date(s); return isValid(d) ? format(d, 'yyyy-MM-dd') : s;
                }
                // Normalize numbers: "1000.00" and 1000 should be equal
                if (!isNaN(Number(s)) && s.trim() !== '') return String(parseFloat(s));
                return s;
              };

              // Applies empty/zero normalization before comparing old and new audit field values.
              const normalizeFieldValue = (value: any) => {
                if (isEmptyOrZero(value)) {
                  return '';
                }
                return normalize(value);
              };

              // Formats audit cell values for display while preserving non-date values.
              const fmt = (v: any) => {
                if (v === null || v === undefined || v === '') return GUEST_DETAIL_FALLBACK.EMPTY;
                if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(v)) {
                  const d = new Date(v); return isValid(d) ? format(d, 'dd MMM yyyy') : v;
                }
                return String(v);
              };

              // Converts legacy Date.toString audit prefixes and ISO values to yyyy-MM-dd text.
              const normalizeAuditDateText = (value: string) => {
                const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
                if (isoMatch) return isoMatch[1];

                const legacyMatch = value.match(/^[A-Za-z]{3}\s+([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})/);
                if (!legacyMatch) return value.trim();

                const months: Record<string, string> = {
                  Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
                  Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
                };
                return `${legacyMatch[3]}-${months[legacyMatch[1]] || legacyMatch[1]}-${legacyMatch[2].padStart(2, '0')}`;
              };

              // Removes legacy date noise and leading separators from summary audit entries.
              const cleanAuditEntry = (entry: string) => entry
                .replace(/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}\s+\S+/, match => normalizeAuditDateText(match))
                .replace(/^,\s*/, '')
                .trim();

              // Splits pipe-delimited summary fields into normalized display entries.
              const getSummaryEntries = (value: any) => {
                const text = fmt(value);
                if (text === GUEST_DETAIL_FALLBACK.EMPTY) return [];
                return text.split(/\s+\|\s+/).map(cleanAuditEntry).filter(Boolean);
              };

              // Extracts stable identity/comparable parts so summary rows show only real differences.
              const getSummaryEntryParts = (key: string, entry: string) => {
                if (key === AUDIT_FIELD_KEY.ROOM_ALLOCATION_SUMMARY) {
                  const pieces = entry.split(',').map(part => part.trim()).filter(Boolean);
                  const date = normalizeAuditDateText(pieces[0] || '');
                  const metricStart = pieces.findIndex(part => /^(Rooms|Adults|Children):/i.test(part));
                  const room = metricStart > 1 ? pieces.slice(1, metricStart).join(', ') : pieces[1] || GUEST_DETAIL_FALLBACK.EMPTY;
                  return {
                    identity: `${date}|${room}`,
                    comparable: metricStart >= 0 ? pieces.slice(metricStart).join('|') : pieces.slice(2).join('|'),
                  };
                }

                if (key === 'DRIVER_AMOUNT_PER_DAY_SUMMARY') {
                  const [datePart, amountPart] = entry.split(/:\s(.+)/);
                  return {
                    identity: normalizeAuditDateText(datePart || ''),
                    comparable: amountPart || '',
                  };
                }

                if (key === 'AMOUNT_PER_PERSON_SUMMARY') {
                  const [left, amount] = entry.split(/:\s(.+)/);
                  const amountMatch = left.match(/^(\d{4}-\d{2}-\d{2})\s+(.+)$/);
                  return {
                    identity: amountMatch ? `${amountMatch[1]}|${amountMatch[2]}` : left,
                    comparable: amount || '',
                  };
                }

                const dateOnly = normalizeAuditDateText(entry);
                return {
                  identity: dateOnly,
                  comparable: dateOnly,
                };
              };

              // Returns only summary entries whose comparable value changed from the opposite audit side.
              const getChangedSummaryEntries = (key: string, value: any, compareValue: any) => {
                const compareMap = new Map(
                  getSummaryEntries(compareValue).map(entry => {
                    const parts = getSummaryEntryParts(key, entry);
                    return [parts.identity, parts.comparable];
                  })
                );

                return getSummaryEntries(value).filter(entry => {
                  const parts = getSummaryEntryParts(key, entry);
                  return compareMap.get(parts.identity) !== parts.comparable;
                });
              };

              // Checks whether either side of a summary field contains a changed, added, or removed entry.
              const hasSummaryDifference = (key: string) =>
                getChangedSummaryEntries(key, (oldObj || {})[key], (newObj || {})[key]).length > 0 ||
                getChangedSummaryEntries(key, (newObj || {})[key], (oldObj || {})[key]).length > 0;

              const changedKeys = a.ACTION === AUDIT_ACTION.UPDATE
                ? allKeys.filter(k => SUMMARY_FIELDS.has(k)
                  ? hasSummaryDifference(k)
                  : normalizeFieldValue((oldObj || {})[k]) !== normalizeFieldValue((newObj || {})[k]))
                : allKeys;

              // Renders scalar or structured summary audit values in a user-readable block layout.
              const renderSummaryValue = (key: string, value: any, compareValue?: any) => {
                if (!SUMMARY_FIELDS.has(key)) return fmt(value);
                const entries = a.ACTION === AUDIT_ACTION.UPDATE
                  ? getChangedSummaryEntries(key, value, compareValue)
                  : getSummaryEntries(value);
                if (!entries.length) return '-';

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {entries.map((entry, index) => {
                      const status = compareValue === undefined
                        ? ''
                        : getSummaryEntries(compareValue).some(compareEntry => getSummaryEntryParts(key, compareEntry).identity === getSummaryEntryParts(key, entry).identity)
                          ? GUEST_DETAIL_AUDIT_LABEL.CHANGED
                          : GUEST_DETAIL_AUDIT_LABEL.ADDED_REMOVED;

                      if (key === AUDIT_FIELD_KEY.ROOM_ALLOCATION_SUMMARY) {
                        const pieces = entry.split(',').map(part => part.trim()).filter(Boolean);
                        const date = normalizeAuditDateText(pieces[0] || '');
                        const metricStart = pieces.findIndex(part => /^(Rooms|Adults|Children):/i.test(part));
                        const room = metricStart > 1 ? pieces.slice(1, metricStart).join(', ') : pieces[1] || GUEST_DETAIL_FALLBACK.EMPTY;
                        const metrics = metricStart >= 0 ? pieces.slice(metricStart) : pieces.slice(2);
                        return (
                          <Box key={`${entry}-${index}`} sx={{ p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.65)' }}>
                            {status && <Typography component="div" fontSize="0.72rem" fontWeight={700}>{status}</Typography>}
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.DATE}:</b> {date || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.ROOM}:</b> {room || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                            <Typography component="div" fontSize="0.78rem">{metrics.join(' | ') || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                          </Box>
                        );
                      }

                      if (key === 'DRIVER_AMOUNT_PER_DAY_SUMMARY') {
                        const [datePart, amountPart] = entry.split(/:\s(.+)/);
                        return (
                          <Box key={`${entry}-${index}`} sx={{ p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.65)' }}>
                            {status && <Typography component="div" fontSize="0.72rem" fontWeight={700}>{status}</Typography>}
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.DATE}:</b> {normalizeAuditDateText(datePart || '') || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.DRIVER_CHARGE}:</b> {amountPart || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                          </Box>
                        );
                      }

                      if (key === 'AMOUNT_PER_PERSON_SUMMARY') {
                        const [left, amount] = entry.split(/:\s(.+)/);
                        const amountMatch = left.match(/^(\d{4}-\d{2}-\d{2})\s+(.+)$/);
                        return (
                          <Box key={`${entry}-${index}`} sx={{ p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.65)' }}>
                            {status && <Typography component="div" fontSize="0.72rem" fontWeight={700}>{status}</Typography>}
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.DATE}:</b> {amountMatch ? amountMatch[1] : GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.ROOM}:</b> {amountMatch ? amountMatch[2] : left || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                            <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.AMOUNT_PER_PERSON}:</b> {amount || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                          </Box>
                        );
                      }

                      const dateOnly = normalizeAuditDateText(entry);
                      return (
                        <Box key={`${entry}-${index}`} sx={{ p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.65)' }}>
                          {status && <Typography component="div" fontSize="0.72rem" fontWeight={700}>{status}</Typography>}
                          <Typography component="div" fontSize="0.78rem"><b>{GUEST_DETAIL_AUDIT_LABEL.STAY_DATE}:</b> {dateOnly || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              };

              const borderColor = a.ACTION === AUDIT_ACTION.CREATE
                ? '#2e7d32'
                : a.ACTION === AUDIT_ACTION.DELETE
                  ? '#c62828'
                  : a.ACTION === AUDIT_ACTION.WHATSAPP_SENT
                    ? '#25D366'
                    : a.ACTION === AUDIT_ACTION.EMAIL_SENT
                      ? '#e65100'
                      : a.ACTION === AUDIT_ACTION.AMOUNT_OVERRIDE
                        ? '#ed6c02'
                        : '#0077b6';
              const chipColor = a.ACTION === AUDIT_ACTION.CREATE
                ? TOAST_SEVERITY.SUCCESS
                : a.ACTION === AUDIT_ACTION.DELETE
                  ? TOAST_SEVERITY.ERROR
                  : a.ACTION === AUDIT_ACTION.WHATSAPP_SENT || a.ACTION === AUDIT_ACTION.EMAIL_SENT
                    ? TOAST_SEVERITY.WARNING
                    : a.ACTION === AUDIT_ACTION.AMOUNT_OVERRIDE
                      ? TOAST_SEVERITY.WARNING
                      : 'primary';
              const chipLabel = a.ACTION === AUDIT_ACTION.WHATSAPP_SENT
                ? AUDIT_DISPLAY.WHATSAPP_SENT_LABEL
                : a.ACTION === AUDIT_ACTION.EMAIL_SENT
                  ? AUDIT_DISPLAY.EMAIL_SENT_LABEL
                  : a.ACTION === AUDIT_ACTION.AMOUNT_OVERRIDE
                    ? AUDIT_DISPLAY.AMOUNT_OVERRIDE_LABEL
                    : a.ACTION;
              return (
                <Paper key={i} sx={{ p: 2, mb: 2, borderLeft: `4px solid ${borderColor}`, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Chip label={chipLabel} color={chipColor} size="small" />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography fontSize="0.8rem" color="text.secondary">{fmtDateTime(a.CHANGED_ON)}</Typography>
                      <Typography fontSize="0.8rem"><b>{GUEST_DETAIL_AUDIT_LABEL.BY}:</b> {a.CHANGED_BY || GUEST_DETAIL_FALLBACK.EMPTY}</Typography>
                    </Box>
                  </Box>

                  {changedKeys.length === 0 ? (
                    <Typography fontSize="0.85rem" color="text.secondary">{GUEST_DETAIL_TEXT.NO_FIELD_CHANGES}</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f7ff' }}>
                          <TableCell sx={{ fontWeight: 700, width: '30%' }}>{GUEST_DETAIL_AUDIT_LABEL.FIELD}</TableCell>
                          {a.ACTION === AUDIT_ACTION.UPDATE ? (
                            <>
                              <TableCell sx={{ fontWeight: 700, color: 'error.main' }}>{GUEST_DETAIL_AUDIT_LABEL.OLD_VALUE}</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>{GUEST_DETAIL_AUDIT_LABEL.NEW_VALUE}</TableCell>
                            </>
                          ) : (
                            <TableCell sx={{ fontWeight: 700 }}>{GUEST_DETAIL_AUDIT_LABEL.VALUE}</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {changedKeys.map(k => (
                          <TableRow key={k} sx={{ '&:hover': { bgcolor: '#f8fbff' } }}>
                            <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{AUDIT_FIELD_LABEL[k] || k}</TableCell>
                            {a.ACTION === AUDIT_ACTION.UPDATE ? (
                              <>
                                <TableCell sx={{ fontSize: '0.82rem', color: 'error.dark', bgcolor: '#fff5f5' }}>{renderSummaryValue(k, (oldObj || {})[k], (newObj || {})[k])}</TableCell>
                                <TableCell sx={{ fontSize: '0.82rem', color: 'success.dark', bgcolor: '#f0fff4' }}>{renderSummaryValue(k, (newObj || {})[k], (oldObj || {})[k])}</TableCell>
                              </>
                            ) : (
                              <TableCell sx={{ fontSize: '0.82rem' }}>{renderSummaryValue(k, (newObj || {})[k])}</TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>{GUEST_DETAIL_TEXT.BUTTON_CLOSE}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog — WhatsApp / Email */}
      <Dialog open={!!confirmDialog} onClose={closeConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {requiresStatusAcknowledgement
            ? `${statusLabel} ${notificationTypeLabel}`
            : confirmDialog?.type === NOTIFICATION_CHANNEL.WHATSAPP ? NOTIFICATION_DIALOG_TITLE.WHATSAPP : NOTIFICATION_DIALOG_TITLE.EMAIL}
        </DialogTitle>
        <DialogContent>
          {requiresStatusAcknowledgement ? (
            <>
              <Typography sx={{ mb: 1.5 }}>
                {confirmDialog?.type === NOTIFICATION_CHANNEL.WHATSAPP
                  ? `${GUEST_DETAIL_CONFIRMATION.SEND_PREFIX} ${booking.STATUS} ${GUEST_DETAIL_CONFIRMATION.WHATSAPP_MESSAGE_SUFFIX} ${booking.GUEST_NAME} (${String(booking.PHONE).replace(/[^0-9]/g, '')})?`
                  : `${GUEST_DETAIL_CONFIRMATION.SEND_PREFIX} ${booking.STATUS} ${GUEST_DETAIL_CONFIRMATION.EMAIL_SUFFIX} ${booking.GUEST_NAME} (${booking.EMAIL})?`}
              </Typography>
              <Alert severity={TOAST_SEVERITY.WARNING} sx={{ mb: 1.5 }}>
                {GUEST_DETAIL_CONFIRMATION.STATUS_ALERT_PREFIX} OBR{booking.BOOKINGID} {GUEST_DETAIL_CONFIRMATION.STATUS_ALERT_MIDDLE} {booking.STATUS}.
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={statusAcknowledged}
                    onChange={e => setStatusAcknowledged(e.target.checked)}
                  />
                }
                label={`${GUEST_DETAIL_CONFIRMATION.ACKNOWLEDGEMENT_PREFIX} ${booking.STATUS} ${notificationTypeLabel} ${GUEST_DETAIL_CONFIRMATION.ACKNOWLEDGEMENT_SUFFIX}`}
              />
            </>
          ) : (
            <Typography>
                {confirmDialog?.type === NOTIFICATION_CHANNEL.WHATSAPP
                  ? `${GUEST_DETAIL_CONFIRMATION.SEND_PREFIX} ${GUEST_DETAIL_CONFIRMATION.BOOKING_CONFIRMATION} ${GUEST_DETAIL_CONFIRMATION.WHATSAPP_MESSAGE_SUFFIX} ${booking.GUEST_NAME} (${String(booking.PHONE).replace(/[^0-9]/g, '')})?`
                  : `${GUEST_DETAIL_CONFIRMATION.SEND_PREFIX} ${GUEST_DETAIL_CONFIRMATION.BOOKING_CONFIRMATION} ${GUEST_DETAIL_CONFIRMATION.EMAIL_SUFFIX} ${booking.GUEST_NAME} (${booking.EMAIL})?`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>{GUEST_DETAIL_TEXT.BUTTON_CANCEL}</Button>
          <Button variant="contained"
            onClick={confirmDialog?.type === NOTIFICATION_CHANNEL.WHATSAPP ? handleSendWhatsApp : handleSendEmail}
            disabled={confirmSendDisabled}
            sx={confirmDialog?.type === NOTIFICATION_CHANNEL.WHATSAPP
              ? { background: '#25D366', '&:hover': { background: '#1ebe57' } }
              : { background: '#e65100', '&:hover': { background: '#bf360c' } }}>
            {NOTIFICATION_BUTTON_LABEL.CONFIRM_SEND}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestDetail;
